import React, { useEffect, useState } from 'react'
import { NavLink } from 'react-router'
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Bell,
  BookOpen,
  Boxes,
  Building,
  Building2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Cloud,
  Code,
  Database,
  Download,
  FileText,
  Grid3X3,
  Home,
  Key,
  // KeyRound, -- restore with the External API Tokens entry below (PENG-13718)
  Layers,
  Layout,
  Link,
  Mail,
  Network,
  Palette,
  Radar,
  ScanEye,
  ScrollText,
  Server,
  ShieldCheck,
  Slack,
  SlidersHorizontal,
  Star,
  Ticket,
  UserCheck,
  Users,
} from 'lucide-react'

// Imported (not referenced as a `/logo/...` public path) so Vite emits it as
// a content-hashed `/assets/Logo-monochrome-white-<hash>.svg`. That matters in
// production: an unhashed public URL keeps the same address across every
// deploy, so a CDN edge or a pod that answered before the file existed can
// keep serving a stale miss indefinitely — and because nginx's SPA fallback
// (`try_files $uri $uri/ /index.html`) answers a missing asset with a 200 HTML
// body, the failure is silent: a background-image that can't decode renders as
// an empty div with nothing in the console. Hashed assets get a new URL per
// content change and are served by the `/assets/` location, which 404s honestly.
//
// `?no-inline` because the file (3.2 KB) is under Vite's 4 KiB
// `assetsInlineLimit` and would otherwise be baked into the bundle as a
// `data:` URI. A real same-origin file survives a `default-src 'self'` CSP;
// a `data:` URI would need an explicit `img-src data:` and would silently
// blank the logo again the moment the security headers are enforced.
import logoWhiteUrl from '../../assets/logo/Logo-monochrome-white.svg?no-inline'
import { useAuth } from '../../contexts/AuthContext'
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext'
import {
  getSidebarSectionCollapse,
  setSidebarSectionCollapse,
} from '../../utils/prefs'

type SidebarProps = {
  isOpen: boolean
  onToggle: () => void
}

type NavItem = {
  to: string
  icon: React.ElementType
  label: string
  badge?: string
  requiredPhase?: 'sprint1' | 'phase2' | 'phase3' | 'phase4'
}

type NavSection = {
  title: string
  items: NavItem[]
  requiredPhase?: 'sprint1' | 'phase2' | 'phase3' | 'phase4'
}

// @purpose Collapsible left nav sidebar building role- and feature-flag-gated navigation sections
const Sidebar = ({ isOpen, onToggle }: SidebarProps): React.JSX.Element => {
  // State to track which sections are collapsed
  const [collapsedSections, setCollapsedSections] = useState<
    Record<string, boolean>
  >({})
  const { isFeatureEnabled } = useFeatureFlags()
  const { isDevelopmentMode, user } = useAuth()

  // Admin access check - check roles from database-enriched groups
  const hasAdminAccess = user?.groups?.some(
    group =>
      group.metadata?.role === 'admin' ||
      group.metadata?.role === 'super_admin',
  )

  // Super admin access check
  const isSuperAdmin = user?.groups?.some(
    group => group.metadata?.role === 'super_admin',
  )

  // Phase Four is now permanently enabled

  // Helper function to check if a required phase is enabled
  const isPhaseEnabled = (
    requiredPhase: 'sprint1' | 'phase2' | 'phase3' | 'phase4' | undefined,
  ): boolean => {
    if (!requiredPhase) return true // No phase requirement means always visible

    switch (requiredPhase) {
      case 'sprint1':
      case 'phase2':
      case 'phase3':
      case 'phase4':
        return true // These phases are now permanently enabled
      default:
        return true
    }
  }

  const navSections = React.useMemo<NavSection[]>(
    () => [
      // Show Development section first when development mode is enabled
      ...(isDevelopmentMode
        ? [
            {
              title: 'Development',
              items: [
                { to: '/docs', icon: BookOpen, label: 'Documentation' },
                {
                  to: '/component-demo',
                  icon: FileText,
                  label: 'Components',
                },
                { to: '/cells-demo', icon: Grid3X3, label: 'Cells' },
                { to: '/cards-demo', icon: Layout, label: 'Cards' },
                { to: '/icons-demo', icon: Palette, label: 'Icons' },
                // Flag-gated separately from the rest of Development: it is a
                // review aid for the GCP work, not a standing dev tool.
                ...(isFeatureEnabled('gcp-surfaces-demo')
                  ? [
                      {
                        to: '/gcp-surfaces-demo',
                        icon: Cloud,
                        label: 'GCP Surfaces',
                      },
                    ]
                  : []),
                {
                  to: '/identity-risk-walkthrough',
                  icon: Network,
                  label: 'Identity Risk Walkthrough',
                },
              ],
            },
          ]
        : []),
      // Dashboard is hidden by default — toggle in Settings > Feature Flags
      ...(isFeatureEnabled('show-dashboard')
        ? [
            {
              title: 'Overview',
              items: [{ to: '/dashboard', icon: Home, label: 'Dashboard' }],
            },
          ]
        : []),
      // ── Discover ─────────────────────────────────────────────────
      // Inventory / catalog of AI assets the platform has discovered.
      {
        title: 'Discover',
        items: [
          {
            to: '/ai-applications/component-catalog',
            icon: Boxes,
            label: 'Component Catalog',
          },
          {
            to: '/ai-applications/catalog',
            icon: Grid3X3,
            label: 'AI Applications',
          },
          ...(isFeatureEnabled('ai-identities')
            ? [
                {
                  to: '/ai-applications/identities',
                  icon: UserCheck,
                  label: 'AI Identities',
                },
              ]
            : []),
        ],
      },
      // ── Detect & Investigate ─────────────────────────────────────
      // Surfacing of active concerns, plus the controls that flag them.
      // "Analyst Console" is the consolidated triage surface (formerly
      // "AI Security Concerns" tabs — Types / Detections / Signals);
      // the URL stays `/ai-applications/security-concerns` so existing
      // bookmarks keep working.
      {
        title: 'Detect & Investigate',
        items: [
          {
            to: '/ai-applications/security-concerns',
            icon: Star,
            label: 'Analyst Console',
          },
          {
            to: '/ai-applications/detection-rules',
            icon: SlidersHorizontal,
            label: 'Detection Rules',
          },
          ...(isFeatureEnabled('ai-security-posture')
            ? [
                {
                  to: '/ai-applications/findings',
                  icon: Activity,
                  label: 'Security Posture',
                },
              ]
            : []),
        ],
      },
      // ── Respond & Contain ────────────────────────────────────────
      // Workflow for resolving active concerns. Incidents is a stub for
      // the new triage surface; the previous tabbed Events+Incidents
      // page has been removed (seccompose migration spec §5 step 1).
      // Guardrails & Policy / Approvals are intentionally omitted
      // (stubs we're deferring).
      {
        title: 'Respond & Contain',
        items: [
          {
            to: '/ai-applications/incidents',
            icon: AlertTriangle,
            label: 'Incidents',
          },
        ],
      },
      // ── Governance & Evidence ────────────────────────────────────
      // CISO/auditor-facing surfaces — exec scorecard + compliance.
      {
        title: 'Governance & Evidence',
        items: [
          ...(isFeatureEnabled('ai-scorecard')
            ? [
                {
                  to: '/ai-applications/scorecard',
                  icon: BarChart3,
                  label: 'CISO Scorecard',
                },
              ]
            : []),
          ...(isFeatureEnabled('ai-compliance')
            ? [
                {
                  to: '/ai-applications/compliance',
                  icon: ShieldCheck,
                  label: 'Compliance',
                },
              ]
            : []),
        ],
      },
      {
        title: 'Cloud Inventory',
        items: [
          // { to: '/inventory', icon: Package, label: 'Network Topology' }, // Temporarily hidden - will be reintroduced shortly
          { to: '/inventory/network', icon: Network, label: 'Network' },
          { to: '/inventory/platform', icon: Layers, label: 'Platform' },
          { to: '/inventory/compute', icon: Server, label: 'Compute' },
          { to: '/inventory/data', icon: Database, label: 'Storage' },
          { to: '/inventory/application', icon: Code, label: 'Application' },
        ],
      },
      // Rule Manager — visible to all authenticated users (M1).
      // Kept outside the hasAdminAccess-gated Administration section
      // because the page must be reachable by non-admin roles.
      ...(isFeatureEnabled('rule-manager')
        ? [
            {
              title: 'Rules',
              items: [
                { to: '/admin/rules', icon: ScrollText, label: 'Rule Manager' },
              ],
            },
          ]
        : []),
      // Phase 3: Integrations
      {
        title: 'Integrations',
        items: [
          ...(isDevelopmentMode
            ? [
                {
                  to: '/integrations/slack',
                  icon: Slack,
                  label: 'Slack Integration',
                },
              ]
            : []),
          // Phase Four features (now permanently enabled)
          {
            to: '/integrations/jira',
            icon: Ticket,
            label: 'Jira Integration',
          },
          // SNOW Integration: nav item added
          {
            to: '/integrations/servicenow',
            icon: Ticket,
            label: 'ServiceNow Integration',
          },
          // ScanEye (not ShieldCheck) — ShieldCheck is the Compliance nav
          // icon under Governance & Evidence; endpoint telemetry reads
          // better here anyway.
          {
            to: '/integrations/defender',
            icon: ScanEye,
            label: 'Defender Integration',
          },
          // The page is administrator-gated server-side, so only admins get
          // the link. The `show-crowdstrike-integration` flag is an
          // additional UI gate (default OFF) so the entry can be hidden from
          // the sidebar entirely without touching the role check or
          // untainting the page route — turning the flag on without admin
          // still shows nothing.
          ...(hasAdminAccess && isFeatureEnabled('show-crowdstrike-integration')
            ? [
                {
                  to: '/integrations/crowdstrike',
                  icon: Radar,
                  label: 'CrowdStrike Integration',
                },
              ]
            : []),
          ...(isDevelopmentMode
            ? [
                {
                  to: '/integrations/http',
                  icon: Link,
                  label: 'HTTP Integration',
                },
              ]
            : []),
        ].filter(item => item), // Remove any undefined items
        requiredPhase: 'phase3',
      },
      // Phase Future: Reporting & Analytics
      ...(isDevelopmentMode
        ? [
            {
              title: 'Reports & Analytics',
              items: [
                { to: '/reports', icon: BarChart3, label: 'Reports' },
                {
                  to: '/export-center',
                  icon: Download,
                  label: 'Export Center',
                },
              ],
            },
          ]
        : []),
      // Phase Future: Alerts
      ...(isDevelopmentMode
        ? [
            {
              title: 'Alerts',
              items: [
                { to: '/notifications', icon: Bell, label: 'Notifications' },
                {
                  to: '/notification-templates',
                  icon: Mail,
                  label: 'Notification Templates',
                },
              ],
            },
          ]
        : []),
      // Administration section - only visible to admin and super_admin roles
      ...(hasAdminAccess
        ? [
            {
              title: 'Administration',
              items: [
                { to: '/admin/users', icon: Users, label: 'User Management' },
                { to: '/admin/api-keys', icon: Key, label: 'API Keys' },
                // Disabled with the external-api service (PENG-13718); the
                // route and its import are commented out in App.tsx too.
                // {
                //   to: '/admin/external-api-tokens',
                //   icon: KeyRound,
                //   label: 'External API Tokens',
                // },
                // Phase Three is now permanent - Business Impact always available
                // {
                //   to: '/admin/business-impact',
                //   icon: TrendingUp,
                //   label: 'Business Impact',
                // },
                // Cloud Services is now permanent - always available to admins
                {
                  to: '/admin/cloud-services',
                  icon: Cloud,
                  label: 'Cloud Services',
                },
                ...(isDevelopmentMode
                  ? [
                      {
                        to: '/admin/roles',
                        icon: UserCheck,
                        label: 'Roles & Permissions',
                      },
                    ]
                  : []),
                ...(isDevelopmentMode
                  ? [
                      {
                        to: '/admin/tenant',
                        icon: Building,
                        label: 'Tenant Settings',
                      },
                    ]
                  : []),
                ...(isDevelopmentMode
                  ? [
                      {
                        to: '/admin/audit',
                        icon: FileText,
                        label: 'Audit Logs',
                      },
                    ]
                  : []),
              ],
            },
          ]
        : []),
      // Global Administration section - only visible to super_admin role
      ...(isSuperAdmin
        ? [
            {
              title: 'Global Administration',
              items: [
                {
                  to: '/admin/org-management',
                  icon: Building2,
                  label: 'Organization Management',
                },
                // Still dev-mode only: promoting the Cloud Services flag made
                // the TENANT-scoped page permanent, not this one. The global
                // page remains Phase Future (see its route in App.tsx), so it
                // keeps its own independent gate.
                ...(isDevelopmentMode
                  ? [
                      {
                        to: '/admin/global-cloud-services',
                        icon: Cloud,
                        label: 'Global Cloud Services',
                      },
                    ]
                  : []),
              ],
            },
          ]
        : []),
    ],
    [isDevelopmentMode, hasAdminAccess, isSuperAdmin, isFeatureEnabled],
  )

  // Load initial collapse state from localStorage
  useEffect(() => {
    const initialState: Record<string, boolean> = {}
    navSections.forEach(section => {
      initialState[section.title] = getSidebarSectionCollapse(section.title)
    })
    setCollapsedSections(initialState)
  }, [navSections])

  // Toggle section collapse state
  const toggleSectionCollapse = (sectionTitle: string) => {
    const newCollapsedState = !collapsedSections[sectionTitle]
    setCollapsedSections(prev => ({
      ...prev,
      [sectionTitle]: newCollapsedState,
    }))
    setSidebarSectionCollapse(sectionTitle, newCollapsedState)
  }

  return (
    <aside
      className={`${isOpen ? 'w-64' : 'w-[82px]'} bg-white dark:bg-header-dark transition-all duration-300 flex flex-col`}
    >
      {/* Header — Deep Blue banner cell. Sits flush with the top Header bar
          (same 78px height) so the two read as one full-width brand band.
          The white logo shows in BOTH modes because the cell is always dark. */}
      <div className="p-4 pt-[13px] bg-header dark:bg-header-dark bg-gradient-to-b from-header-from to-header-to dark:from-header-dark-from dark:to-header-dark-to border-b border-r border-header-line dark:border-header-accent/40 dark:border-r-header-edge h-[60px]">
        <div className="flex items-center justify-between">
          {isOpen && (
            <div className="flex items-center space-x-2">
              <div
                className="w-[175px] h-[35px] bg-contain"
                style={{ backgroundImage: `url(${logoWhiteUrl})` }}
                role="img"
                aria-label="RoonCyber.AI"
              />
            </div>
          )}
          {!isOpen && (
            <div className="flex items-center space-x-2">
              <div
                className="ml-[0px] w-[35px] h-[35px] bg-cover"
                style={{ backgroundImage: `url(${logoWhiteUrl})` }}
                role="img"
                aria-label="RoonCyber.AI"
              />
            </div>
          )}
          <button
            onClick={onToggle}
            className="ml-auto w-[26px] p-1.5 rounded-lg text-header-icon hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Toggle Sidebar"
          >
            {isOpen ? (
              <ChevronLeft className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-4 border-r border-slate-200 dark:border-header-edge">
        <div className="space-y-8">
          {navSections
            .filter(section => {
              // Check if section's required phase is enabled
              if (!isPhaseEnabled(section.requiredPhase)) {
                return false
              }
              return section.items.length > 0
            })
            .map((section, sectionIndex) => {
              const isCollapsed = collapsedSections[section.title] || false

              return (
                <div key={sectionIndex}>
                  {isOpen && (
                    <button
                      onClick={() => toggleSectionCollapse(section.title)}
                      className="flex items-center justify-between w-full text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3 hover:text-slate-600 dark:hover:text-slate-300 transition-colors whitespace-nowrap"
                    >
                      <span className="whitespace-nowrap">{section.title}</span>
                      {isCollapsed ? (
                        <ChevronRight className="w-3 h-3" />
                      ) : (
                        <ChevronDown className="w-3 h-3" />
                      )}
                    </button>
                  )}
                  {(!isCollapsed || !isOpen) && (
                    <ul className="space-y-1">
                      {section.items
                        .filter(item => {
                          // Check if item's required phase is enabled
                          return isPhaseEnabled(item.requiredPhase)
                        })
                        .map((item, itemIndex) => {
                          return (
                            <li key={itemIndex}>
                              <NavLink
                                to={item.to}
                                title={!isOpen ? item.label : undefined} // Show tooltip when collapsed
                                className={({ isActive }) =>
                                  `flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                                    isActive
                                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                                      : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5'
                                  }`
                                }
                              >
                                <item.icon className="w-5 h-5 flex-shrink-0" />
                                {isOpen && (
                                  <>
                                    <span className="flex-1 whitespace-nowrap">
                                      {item.label}
                                    </span>
                                    {item.badge && (
                                      <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                                        {item.badge}
                                      </span>
                                    )}
                                  </>
                                )}
                              </NavLink>
                            </li>
                          )
                        })}
                    </ul>
                  )}
                </div>
              )
            })}
        </div>
      </nav>
    </aside>
  )
}

export default Sidebar
